import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui";
import { useDataManagementFileOperations } from "./useDataManagementFileOperations";

interface DataManagementHeaderProps {
  onFileUploaded: () => void;
}

export const DataManagementHeader = ({
  onFileUploaded,
}: DataManagementHeaderProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, uploadLoading } =
    useDataManagementFileOperations(onFileUploaded);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadFile(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const buttonText = uploadLoading
    ? t("dataManagement.upload.uploading")
    : t("dataManagement.upload.button");

  return (
    <>
      <section
        className="flex items-center justify-between border-b p-6 pb-4"
        role="region"
        data-testid="data-management-header"
        aria-labelledby="data-management-title"
      >
        <h1 id="data-management-title" className="text-2xl font-bold">
          {t("dataManagement.title")}
        </h1>
        <Button
          onClick={handleUploadClick}
          disabled={uploadLoading}
          data-testid="upload-file-button"
        >
          <Upload className="mr-2 h-4 w-4" />
          {buttonText}
        </Button>
      </section>

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileUpload}
        style={{ display: "none" }}
        data-testid="file-input"
      />
    </>
  );
};
